import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, XCircle, Upload, AlertCircle, DollarSign, CreditCard, Save } from 'lucide-react';

import { API_URL } from '../config';
const API = `${API_URL}/api`;

function AffiliateStatus() {
  const [email, setEmail] = useState('');
  const [affiliateCode, setAffiliateCode] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Document upload states
  const [w9File, setW9File] = useState(null);
  const [idFile, setIdFile] = useState(null);
  const [uploadingW9, setUploadingW9] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);
  
  // Payment info states
  const [paypalInfo, setPaypalInfo] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentSaved, setPaymentSaved] = useState(false);

  const checkStatus = async () => {
    if (!email && !affiliateCode) {
      setError('Please enter your email or affiliate code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/affiliate/check-status`, {
        email: email || undefined,
        affiliate_code: affiliateCode || undefined
      });

      setStatus(response.data);
      // Load existing PayPal info if available
      if (response.data.paypal_info) {
        setPaypalInfo(response.data.paypal_info);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to check status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const savePaymentInfo = async () => {
    if (!paypalInfo.trim()) {
      alert('Please enter your PayPal email or PayPal.me link');
      return;
    }

    setSavingPayment(true);
    setPaymentSaved(false);

    try {
      await axios.post(`${API}/affiliate/${status.affiliate_code}/payment-info`, {
        paypal_info: paypalInfo.trim()
      });

      setPaymentSaved(true);
      setTimeout(() => setPaymentSaved(false), 3000);
      
      // Update local status
      setStatus(prev => ({ ...prev, paypal_info: paypalInfo.trim() }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save payment info');
    } finally {
      setSavingPayment(false);
    }
  };

  const uploadW9 = async () => {
    if (!w9File) return;

    setUploadingW9(true);
    const formData = new FormData();
    formData.append('file', w9File);

    try {
      await axios.post(
        `${API}/affiliate/${status.affiliate_code}/upload-w9`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      alert('W-9 uploaded successfully!');
      // Refresh status
      checkStatus();
      setW9File(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to upload W-9');
    } finally {
      setUploadingW9(false);
    }
  };

  const uploadId = async () => {
    if (!idFile) return;

    setUploadingId(true);
    const formData = new FormData();
    formData.append('file', idFile);

    try {
      await axios.post(
        `${API}/affiliate/${status.affiliate_code}/upload-id`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      alert('ID document uploaded successfully!');
      // Refresh status
      checkStatus();
      setIdFile(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to upload ID');
    } finally {
      setUploadingId(false);
    }
  };

  const getStatusBadge = (appStatus) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500', icon: Clock, text: 'Pending Review' },
      approved: { color: 'bg-green-500', icon: CheckCircle2, text: 'Approved' },
      rejected: { color: 'bg-red-500', icon: XCircle, text: 'Rejected' }
    };

    const config = statusConfig[appStatus] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="h-4 w-4 mr-1" />
        {config.text}
      </Badge>
    );
  };

  const getVerificationBadge = (verStatus) => {
    const statusConfig = {
      pending: { color: 'bg-gray-400', text: 'Documents Not Submitted' },
      documents_submitted: { color: 'bg-blue-500', text: 'Under Review' },
      verified: { color: 'bg-green-500', text: 'Verified ✓' },
      rejected: { color: 'bg-red-500', text: 'Documents Rejected' }
    };

    const config = statusConfig[verStatus] || statusConfig.pending;

    return (
      <Badge className={`${config.color} text-white`}>
        {config.text}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-8 text-slate-900">
          Check Application Status
        </h1>

        {/* Status Checker Form */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Enter Your Information</CardTitle>
            <CardDescription>Check your affiliate application status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email Address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>

              <div className="text-center text-slate-600">- OR -</div>

              <div>
                <label className="block text-sm font-medium mb-2">Affiliate Code</label>
                <Input
                  type="text"
                  value={affiliateCode}
                  onChange={(e) => setAffiliateCode(e.target.value)}
                  placeholder="AFF-XXXXX"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <Button
                onClick={checkStatus}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? 'Checking...' : 'Check Status'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Status Display */}
        {status && (
          <div className="space-y-6">
            {/* Application Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Application Status
                  {getStatusBadge(status.application_status)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Name</p>
                    <p className="font-semibold">{status.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="font-semibold">{status.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Affiliate Code</p>
                    <p className="font-semibold">{status.affiliate_code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Total Followers</p>
                    <p className="font-semibold">{status.total_followers.toLocaleString()}</p>
                  </div>
                </div>

                {status.applied_at && (
                  <div>
                    <p className="text-sm text-slate-600">Applied On</p>
                    <p className="font-semibold">
                      {new Date(status.applied_at).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {status.approved_at && (
                  <div>
                    <p className="text-sm text-slate-600">Approved On</p>
                    <p className="font-semibold">
                      {new Date(status.approved_at).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {status.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 p-4 rounded">
                    <p className="text-sm font-semibold text-red-900 mb-1">Rejection Reason:</p>
                    <p className="text-red-700">{status.rejection_reason}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Document Upload Section - Only show if approved */}
            {status.application_status === 'approved' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    Document Verification
                    {getVerificationBadge(status.verification_status)}
                  </CardTitle>
                  <CardDescription>
                    Upload required documents to receive payouts
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {!status.can_receive_payouts && (
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded flex items-start">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
                      <div>
                        <p className="font-semibold text-yellow-900">Documents Required</p>
                        <p className="text-sm text-yellow-800 mt-1">
                          Please upload your W-9 form and government-issued ID to enable payouts.
                        </p>
                      </div>
                    </div>
                  )}

                  {status.can_receive_payouts && (
                    <div className="bg-green-50 border border-green-200 p-4 rounded flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-900">Verification Complete!</p>
                        <p className="text-sm text-green-800 mt-1">
                          You can now receive payouts. Access your dashboard to track earnings.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* W-9 Upload */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium">W-9 Tax Form</label>
                      {status.w9_uploaded && (
                        <Badge className="bg-green-500 text-white">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Uploaded
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setW9File(e.target.files[0])}
                        disabled={status.w9_uploaded || uploadingW9}
                      />
                      <Button
                        onClick={uploadW9}
                        disabled={!w9File || status.w9_uploaded || uploadingW9}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingW9 ? 'Uploading...' : 'Upload'}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Accepted formats: PDF, JPG, PNG
                    </p>
                  </div>

                  {/* ID Upload */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium">Government-Issued ID</label>
                      {status.id_uploaded && (
                        <Badge className="bg-green-500 text-white">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Uploaded
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => setIdFile(e.target.files[0])}
                        disabled={status.id_uploaded || uploadingId}
                      />
                      <Button
                        onClick={uploadId}
                        disabled={!idFile || status.id_uploaded || uploadingId}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingId ? 'Uploading...' : 'Upload'}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Driver's License, Passport, or State ID
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment Information Section - Only show if approved */}
            {status.application_status === 'approved' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    Payment Information
                  </CardTitle>
                  <CardDescription>
                    Add your payment details to receive commission payouts
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* PayPal Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="#003087">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.773.773 0 0 1 .763-.642h6.508c2.159 0 3.818.548 4.927 1.629.473.462.829 1.003 1.057 1.61.244.65.351 1.4.318 2.229-.012.293-.036.586-.072.876-.271 2.138-1.205 3.812-2.775 4.98-1.48 1.1-3.405 1.658-5.723 1.658H7.795a.773.773 0 0 0-.763.642l-.956 5.635z"/>
                        <path d="M18.429 7.178c-.012.293-.036.586-.072.876-.271 2.138-1.205 3.812-2.775 4.98-1.48 1.1-3.405 1.658-5.723 1.658H7.795a.773.773 0 0 0-.763.642l-1.283 7.561h3.093l.817-4.818a.773.773 0 0 1 .763-.642h2.064c2.318 0 4.243-.558 5.723-1.658 1.57-1.168 2.504-2.842 2.775-4.98.036-.29.06-.583.072-.876.033-.829-.074-1.579-.318-2.229-.228-.607-.584-1.148-1.057-1.61-.246-.24-.521-.453-.821-.64.012.093.023.186.032.28.033.829-.074 1.579-.318 2.229-.228.607-.584 1.148-1.057 1.61-.246.24-.521.453-.821.64-.686.426-1.486.748-2.383.96z" fill="#0070ba"/>
                      </svg>
                      <h3 className="font-semibold text-blue-900">PayPal</h3>
                      {status.paypal_info && (
                        <Badge className="bg-green-500 text-white ml-auto">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Saved
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-sm text-blue-800 mb-3">
                      Enter your PayPal email address or PayPal.me link to receive payments
                    </p>
                    
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={paypalInfo}
                        onChange={(e) => setPaypalInfo(e.target.value)}
                        placeholder="your@email.com or paypal.me/yourname"
                        className="flex-1 bg-white"
                      />
                      <Button
                        onClick={savePaymentInfo}
                        disabled={savingPayment || !paypalInfo.trim()}
                        className={paymentSaved ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
                      >
                        {paymentSaved ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Saved!
                          </>
                        ) : savingPayment ? (
                          'Saving...'
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save
                          </>
                        )}
                      </Button>
                    </div>
                    
                    <p className="text-xs text-blue-600 mt-2">
                      💡 Tip: PayPal.me links look like: paypal.me/yourname
                    </p>
                  </div>

                  {/* Stripe Section */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard className="h-5 w-5 text-slate-600" />
                      <h3 className="font-semibold text-slate-700">Stripe (Bank Transfer)</h3>
                    </div>
                    <p className="text-sm text-slate-600">
                      Stripe bank transfers are also available. Contact support to set up Stripe Connect for direct bank deposits.
                    </p>
                  </div>

                  {/* Payout Info */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">💰 Payout Details</h4>
                    <ul className="text-sm text-green-800 space-y-1">
                      <li>• Minimum payout: <strong>$100</strong></li>
                      <li>• Payouts processed within 2-3 business days</li>
                      <li>• You can have both PayPal and Stripe on file</li>
                      <li>• Admin will choose payment method based on your preference</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Social Media Info */}
            {status.social_media && status.social_media.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Social Media Profiles</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {status.social_media.map((sm, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                        <div>
                          <p className="font-semibold">{sm.platform}</p>
                          <p className="text-sm text-slate-600">@{sm.username}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-blue-600">
                            {sm.follower_count.toLocaleString()}
                          </p>
                          <p className="text-xs text-slate-500">followers</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
    </div>
  );
}

export default AffiliateStatus;
